import { useCallback, useEffect, useMemo, useState } from 'react';
import { useBusiness } from '../contexts/BusinessContext';
import { getOrders, type Order } from '../lib/purchaseOrders';

export interface PurchaseOrderSnippetItem {
  id: string;
  orderNumber: string;
  supplierName: string;
  status: Order['status'];
  totalAmount: number;
  itemCount: number;
  receivedCount: number;
  createdAt: string | null;
}

function toSnippetItem(order: Order): PurchaseOrderSnippetItem {
  const items = order.items ?? [];
  const receivedCount = items.filter((item) => (item.received_quantity ?? 0) >= item.quantity).length;
  const supplier = order.supplier;
  const supplierName = supplier
    ? [supplier.first_name, supplier.last_name].filter(Boolean).join(' ') || 'Unknown supplier'
    : 'Unknown supplier';

  return {
    id: order.id!,
    orderNumber: order.order_number,
    supplierName,
    status: order.status,
    totalAmount: order.total_amount,
    itemCount: items.length,
    receivedCount,
    createdAt: order.created_at ?? null,
  };
}

/** Pending purchase orders for the control-centre inventory widget. */
export function usePurchaseOrdersSnippetData() {
  const { selectedBusiness } = useBusiness();
  const businessId = selectedBusiness?.id ?? '';

  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!businessId) {
      setOrders([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const data = await getOrders(businessId);
      setOrders(data);
    } catch (err) {
      console.error('Error loading purchase orders snippet:', err);
      setError(err instanceof Error ? err.message : 'Failed to load purchase orders');
      setOrders([]);
    } finally {
      setLoading(false);
    }
  }, [businessId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const pendingOrders = useMemo(
    () =>
      orders
        .filter((order) => order.status === 'pending')
        .slice()
        .sort((a, b) => (b.created_at ?? '').localeCompare(a.created_at ?? '')),
    [orders]
  );

  const purchaseOrders = useMemo(() => pendingOrders.map(toSnippetItem), [pendingOrders]);

  const pendingCount = purchaseOrders.length;

  return {
    orders,
    purchaseOrders,
    pendingOrders,
    pendingCount,
    loading,
    error,
    refresh,
  };
}
